import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { loadFromLocalStorage } from 'utils/localStorageHelpers';

// A 900px box-resampled cut of login-cover.png (which is 3244px wide). Letting
// the browser scale the original down to a ~150px header mark aliased the
// palette PNG badly; this is ~3x the largest on-page size and stays crisp.
import logo from 'assets/icons/logo-mark.png';
// Reversed lockup: identical mark with the black "Auto Parts Supply" script
// knocked out to white, for use while the header sits over the navy hero.
import logoReversed from 'assets/icons/logo-mark-reversed.png';

import { BRAND_LOGOS, CATEGORIES, MAKERS } from './assets';
import {
  ABOUT_POINTS,
  AUDIENCE,
  BRAND,
  CONTACT,
  FAQ,
  FEATURES,
  FOOTER_LINKS,
  HEADER_LINKS,
  HERO_POINTS,
  NAV_LINKS,
  ORIGINS,
  SEO,
  SIGN_IN_PATH,
  SITE_URL,
  STEPS,
} from './content';
import {
  IconArrow,
  IconBolt,
  IconCart,
  IconCheck,
  IconClock,
  IconClose,
  IconCode,
  IconDocument,
  IconGlobe,
  IconLogin,
  IconMenu,
  IconPhone,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconSend,
  IconShield,
  IconTruck,
  IconUser,
  IconWallet,
  RotorMark,
} from './icons';

import './index.scss';

const ICONS = {
  phone: IconPhone,
  search: IconSearch,
  code: IconCode,
  photo: IconPhoto,
  document: IconDocument,
  wallet: IconWallet,
  bolt: IconBolt,
  user: IconUser,
  cart: IconCart,
  send: IconSend,
  truck: IconTruck,
  shield: IconShield,
  globe: IconGlobe,
};

const Glyph = ({ name, ...rest }) => {
  const Component = ICONS[name];
  return Component ? <Component {...rest} /> : null;
};

/**
 * Adds `is-visible` to every `[data-reveal]` node once it scrolls into view, so
 * the CSS can do the actual animating. One shared observer for the whole page
 * instead of a ref per section, and it degrades to "everything visible" when
 * IntersectionObserver is missing or the user asked for reduced motion.
 */
const useReveal = () => {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('[data-reveal]'));
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);
};

const SignInButton = ({ signedIn, variant = 'primary', size = 'md', block = false, label, className = '' }) => {
  const to = signedIn ? '/makers' : SIGN_IN_PATH;
  const text = label || (signedIn ? 'ادخل إلى المنصة' : 'تسجيل الدخول');

  return (
    <Link
      to={to}
      className={`ed-btn ed-btn--${variant} ed-btn--${size} ${block ? 'ed-btn--block' : ''} ${className}`}
    >
      <IconLogin className="ed-btn__icon" />
      <span>{text}</span>
    </Link>
  );
};

/** Opening an account starts with a phone call, so this sits beside every sign-in. */
// Defaults to `outline`, which is legible on light and tinted sections. `ghost`
// is white-on-translucent and only works over the navy blocks — passing it on a
// light background renders the button all but invisible.
const CallButton = ({ variant = 'outline', size = 'md', block = false, showNumber = true, className = '' }) => (
  <a
    href={`tel:${CONTACT.tel}`}
    className={`ed-btn ed-btn--${variant} ed-btn--${size} ${block ? 'ed-btn--block' : ''} ${className}`}
  >
    <IconPhone className="ed-btn__icon" />
    <span>اتصل بينا</span>
    {showNumber && (
      <span className="ed-btn__num" dir="ltr">
        {CONTACT.phone}
      </span>
    )}
  </a>
);

const SectionHead = ({ eyebrow, title, lead, align = 'center' }) => (
  <header className={`ed-head ed-head--${align}`} data-reveal>
    {eyebrow && <span className="ed-eyebrow">{eyebrow}</span>}
    <h2 className="ed-head__title">{title}</h2>
    {lead && <p className="ed-head__lead">{lead}</p>}
  </header>
);

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const frame = useRef(null);

  const signedIn = useMemo(() => Boolean(loadFromLocalStorage('authHeaders')), []);

  /* Search engines key on `alternateName` to connect the brand people actually
     type ("Eldoctor", "الدكتور") to the registered company name. The FAQPage
     block makes the questions below eligible for rich results. No address or
     phone is published — deliberately. */
  const structuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: BRAND.knownAs,
          legalName: BRAND.legalName,
          alternateName: [
            'Eldoctor',
            'El Doctor',
            'الدكتور',
            'El Doctor Auto Parts Supply',
            BRAND.legalNameLatin,
          ],
          description: SEO.description,
          url: `${SITE_URL}/`,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.png`, width: 1200, height: 630 },
          foundingDate: BRAND.since,
          telephone: CONTACT.tel,
          knowsAbout: MAKERS.map((maker) => maker.latin),
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: CONTACT.tel,
            contactType: 'sales',
            availableLanguage: ['ar', 'en'],
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: CONTACT.openDays,
              opens: CONTACT.opens,
              closes: CONTACT.closes,
            },
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: `${BRAND.knownAs} — ${BRAND.latin}`,
          inLanguage: 'ar',
          publisher: { '@id': `${SITE_URL}/#organization` },
        },
        // Spells out the range in the terms people search, so Google can match
        // the page to "قطع غيار <make>" and to the part categories by name.
        {
          '@type': 'ItemList',
          '@id': `${SITE_URL}/#catalogue`,
          name: 'قطع غيار السيارات المتوفرة لدى الدكتور',
          itemListElement: [
            ...MAKERS.map((maker, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: `قطع غيار ${maker.ar} — ${maker.latin}`,
            })),
            ...CATEGORIES.map((category, index) => ({
              '@type': 'ListItem',
              position: MAKERS.length + index + 1,
              name: `${category.ar} — قطع غيار سيارات`,
            })),
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE_URL}/#faq`,
          mainEntity: FAQ.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      ],
    }),
    [],
  );

  useReveal();

  useEffect(() => {
    const onScroll = () => {
      if (frame.current) return;
      frame.current = window.requestAnimationFrame(() => {
        setScrolled(window.pageYOffset > 24);
        frame.current = null;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame.current) window.cancelAnimationFrame(frame.current);
    };
  }, []);

  // The drawer covers the viewport, so let it own the scroll while it is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  /* Anchor navigation is handled here rather than with `scroll-behavior: smooth`,
     which can only be set on the document element and would leak into the rest of
     the app. Doing it manually also lets the fixed header keep its own offset. */
  const scrollTo = useCallback(
    (event, id) => {
      event.preventDefault();
      setMenuOpen(false);

      const target = id === 'top' ? null : document.getElementById(id);
      const top = target ? target.getBoundingClientRect().top + window.pageYOffset - 72 : 0;
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({ top: Math.max(top, 0), behavior: reduced ? 'auto' : 'smooth' });
    },
    [],
  );

  const toggleFaq = useCallback((index) => {
    setOpenFaq((current) => (current === index ? -1 : index));
  }, []);

  return (
    <div className="ed-landing" dir="rtl" lang="ar">
      {/* Scoped to this screen only — deliberately not setting <html dir>, so the
          LTR authentication screens are unaffected when the landing unmounts.
          The same tags are also hard-coded in public/index.html so that crawlers
          which do not execute JS still get them. */}
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="keywords" content={SEO.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="theme-color" content="#16325c" />
        <link rel="canonical" href={`${SITE_URL}/`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={`${BRAND.knownAs} — ${BRAND.latin}`} />
        <meta property="og:locale" content="ar_EG" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* ---------------------------------------------------------------- header */}
      <header className={`ed-nav ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="ed-shell ed-nav__inner">
          {/* Two lockups cross-faded by the scrolled state: reversed over the navy
              hero, full colour once the bar turns white. */}
          <a href="#top" className="ed-nav__brand" aria-label={BRAND.knownAs} onClick={(e) => scrollTo(e, 'top')}>
            <img
              src={logoReversed}
              alt={`${BRAND.latin} — ${BRAND.tagline}`}
              className="ed-nav__logo ed-nav__logo--reversed"
            />
            <img src={logo} alt="" aria-hidden="true" className="ed-nav__logo ed-nav__logo--colour" />
          </a>

          <nav className="ed-nav__links" aria-label="روابط الصفحة">
            {HEADER_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className="ed-nav__link"
                onClick={(e) => scrollTo(e, link.id)}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="ed-nav__actions">
            <SignInButton signedIn={signedIn} size="sm" className="ed-nav__cta" />
            <button
              type="button"
              className="ed-nav__burger"
              onClick={() => setMenuOpen(true)}
              aria-label="فتح القائمة"
            >
              <IconMenu />
            </button>
          </div>
        </div>
      </header>

      <div className={`ed-drawer ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <div className="ed-drawer__scrim" onClick={closeMenu} />
        <div className="ed-drawer__panel">
          <div className="ed-drawer__top">
            <img src={logo} alt={BRAND.latin} className="ed-drawer__logo" />
            <button type="button" className="ed-drawer__close" onClick={closeMenu} aria-label="إغلاق القائمة">
              <IconClose />
            </button>
          </div>
          <nav className="ed-drawer__links">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className="ed-drawer__link"
                onClick={(e) => scrollTo(e, link.id)}
              >
                <span>{link.label}</span>
                <IconArrow className="ed-drawer__chevron" />
              </a>
            ))}
          </nav>
          <SignInButton signedIn={signedIn} size="lg" block />
          <CallButton size="lg" variant="outline" block />
        </div>
      </div>

      {/* ------------------------------------------------------------------ hero */}
      <section className="ed-hero" id="top">
        <div className="ed-hero__grid" aria-hidden="true" />
        <div className="ed-hero__glow" aria-hidden="true" />
        <div className="ed-hero__glow ed-hero__glow--blue" aria-hidden="true" />
        <RotorMark className="ed-hero__rotor" />
        <RotorMark className="ed-hero__rotor ed-hero__rotor--small" />

        <div className="ed-shell ed-hero__inner">
          <div className="ed-hero__copy" data-reveal>
            <span className="ed-hero__eyebrow">
              <span className="ed-dot" />
              {BRAND.legalName}
            </span>

            {/* The brand sits inside the h1 on purpose — "الدكتور / El Doctor" is
                the term customers actually search for. */}
            <h1 className="ed-hero__title">
              <span className="ed-hero__title-brand">
                «{BRAND.knownAs}» · {BRAND.latin}
              </span>{' '}
              كل قطعة غيار بتدوّر عليها{' '}
              <span className="ed-hero__title-accent">في منصة واحدة</span>
            </h1>

            <p className="ed-hero__lead">
              توريد قطع غيار السيارات للمحلات والورش وتجار الجملة من سنة {BRAND.since} — كتالوج كامل بالماركة
              والموديل والفئة، وطلب أونلاين في أي وقت.
            </p>

            <div className="ed-hero__cta">
              <SignInButton signedIn={signedIn} size="lg" />
              <CallButton size="lg" variant="ghost" />
            </div>

            <ul className="ed-hero__points">
              {HERO_POINTS.map((point) => (
                <li key={point}>
                  <IconCheck className="ed-hero__check" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <aside className="ed-hero__card" data-reveal>
            <div className="ed-hero__card-logo">
              <img src={logo} alt={`${BRAND.latin} — ${BRAND.tagline}`} />
            </div>
            <div className="ed-hero__card-body">
              <span className="ed-hero__card-label">بوابة العملاء</span>
              <p className="ed-hero__card-text">
                حسابك عندك بيفتح على أسعارك وعروضك ورصيدك — من غير مكالمات ولا كشوف ورق.
              </p>
              <SignInButton signedIn={signedIn} size="md" block />
              <CallButton size="md" variant="ghost" block showNumber={false} />
              <span className="ed-hero__card-note">لسه من غير حساب؟ اتصل بينا ونفتحه لك.</span>
            </div>
          </aside>
        </div>

        <div className="ed-hero__makers">
          <div className="ed-shell ed-makers">
            <span className="ed-makers__label">تخصّصنا</span>
            <ul className="ed-makers__list">
              {MAKERS.map((maker) => (
                <li key={maker.slug}>{maker.latin}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------- features */}
      <section className="ed-section" id="why">
        <div className="ed-shell">
          <SectionHead
            eyebrow="ليه الدكتور"
            title="منصة اتبنت على شغل قطع الغيار، مش على شكله بس"
            lead="كل حاجة في المنصة مترتّبة زي ما الورشة والمحل بيفكّروا: ماركة، موديل، فئة، كود."
          />

          <div className="ed-cards">
            {FEATURES.map((feature, index) => (
              <article className="ed-card" key={feature.title} data-reveal style={{ '--i': index }}>
                <span className="ed-card__icon">
                  <Glyph name={feature.icon} />
                </span>
                <h3 className="ed-card__title">{feature.title}</h3>
                <p className="ed-card__body">{feature.body}</p>
              </article>
            ))}
          </div>

          <div className="ed-inline-cta" data-reveal>
            <p>حسابك جاهز؟ ابدأ الطلب دلوقتي.</p>
            <SignInButton signedIn={signedIn} />
            <CallButton variant="outline" />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ steps */}
      <section className="ed-section ed-section--dark" id="how">
        <div className="ed-section__grid" aria-hidden="true" />
        <div className="ed-shell">
          <SectionHead
            eyebrow="إزاي تطلب"
            title="من تسجيل الدخول لحد إرسال الطلبية — أربع خطوات"
            lead="من غير تدريب ولا تعقيد. لو بتعرف تدوّر على قطعة، بتعرف تستخدم المنصة."
          />

          <ol className="ed-steps">
            {STEPS.map((step, index) => (
              <li className="ed-step" key={step.title} data-reveal style={{ '--i': index }}>
                <span className="ed-step__num">{`0${index + 1}`}</span>
                <span className="ed-step__icon">
                  <Glyph name={step.icon} />
                </span>
                <h3 className="ed-step__title">{step.title}</h3>
                <p className="ed-step__body">{step.body}</p>
              </li>
            ))}
          </ol>

          <div className="ed-steps__cta" data-reveal>
            <CallButton size="lg" variant="light" />
            <SignInButton signedIn={signedIn} size="lg" variant="ghost" />
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------- audience */}
      <section className="ed-section ed-section--tint">
        <div className="ed-shell">
          <SectionHead eyebrow="لمين المنصة" title="اتعملت لكل حد بيتعامل مع قطع الغيار كل يوم" />

          <div className="ed-audience">
            {AUDIENCE.map((item, index) => (
              <article className="ed-audience__item" key={item.title} data-reveal style={{ '--i': index }}>
                <span className="ed-audience__icon">
                  <Glyph name={item.icon} />
                </span>
                <div>
                  <h3 className="ed-audience__title">{item.title}</h3>
                  <p className="ed-audience__body">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------------- makers */}
      <section className="ed-section" id="makers">
        <div className="ed-shell">
          {/* Headings carry the phrases people actually type. "قطع غيار" on its
              own is far too competitive for a single page, but "قطع غيار هيونداي"
              and friends are winnable — so they appear as real headings here
              rather than only in alt text. */}
          <SectionHead
            eyebrow="الماركات"
            title="قطع غيار هيونداي وكيا وشيفروليه وسانج يونج ونيسان"
            lead="كتالوج قطع غيار مبني موديل موديل حول الماركات الكورية والآسيوية — مش قطع عامة على كل حاجة."
          />

          <ul className="ed-makers-grid">
            {MAKERS.map((maker, index) => (
              <li className="ed-maker" key={maker.slug} data-reveal style={{ '--i': index }}>
                <span className="ed-maker__plate">
                  <img
                    src={maker.src}
                    alt={`قطع غيار ${maker.ar} — ${maker.latin}`}
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="240"
                  />
                </span>
                <h3 className="ed-maker__ar">
                  قطع غيار <span>{maker.ar}</span>
                </h3>
                <span className="ed-maker__latin">{maker.latin}</span>
              </li>
            ))}
          </ul>

        </div>
      </section>

      {/* --------------------------------------------------------------- brands */}
      <section className="ed-section ed-section--tint" id="brands">
        <div className="ed-shell">
          <SectionHead
            eyebrow="ماركات القطع"
            title="بيوت تصنيع عالمية، ومنشأ محدّد لكل صنف"
            lead="بنتعامل مع ماركات معروفة في السوق، وكل صنف مكتوب منشأه قدامك قبل ما تطلبه."
          />
        </div>

        {/* Two rows drifting in opposite directions so the wall reads as motion
            rather than as one long conveyor belt. */}
        {[0, 1].map((row) => (
          <div className={`ed-wall ed-wall--${row === 0 ? 'fwd' : 'rev'}`} key={row} data-reveal>
            <div className="ed-wall__track">
              {[0, 1].map((copy) => (
                <ul className="ed-wall__row" key={copy} aria-hidden={copy === 1}>
                  {BRAND_LOGOS.filter((_, index) => index % 2 === row).map((brand) => (
                    <li className="ed-logo" key={`${copy}-${brand.slug}`}>
                      <img
                        src={brand.src}
                        alt={brand.label}
                        title={brand.label}
                        loading="lazy"
                        decoding="async"
                      />
                    </li>
                  ))}
                </ul>
              ))}
            </div>
            <span className="ed-wall__fade ed-wall__fade--start" aria-hidden="true" />
            <span className="ed-wall__fade ed-wall__fade--end" aria-hidden="true" />
          </div>
        ))}

        <div className="ed-shell">
          <div className="ed-origins" data-reveal>
            <span className="ed-origins__label">بلاد المنشأ</span>
            <ul className="ed-origins__list">
              {ORIGINS.map((origin) => (
                <li key={origin}>{origin}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ about */}
      {/* Plain white: the brands section above is already tinted, and two tinted
          bands back to back read as one oversized grey block. */}
      <section className="ed-section" id="about">
        <div className="ed-shell ed-about">
          <div className="ed-about__copy" data-reveal>
            <span className="ed-eyebrow">من نحن</span>
            <h2 className="ed-about__title">
              {BRAND.legalName}
              <span className="ed-about__alias">
                المعروفة باسم «{BRAND.knownAs}» — {BRAND.latin}
              </span>
              <span className="ed-about__latin">{BRAND.legalNameLatin}</span>
            </h2>
            <p className="ed-about__lead">
              بنشتغل في توريد قطع غيار السيارات للمحلات والورش وتجار الجملة، بتخصّص واضح في الماركات الكورية
              والآسيوية. الفكرة بسيطة: كتالوج مترتّب، أصناف موثوقة، وطريقة طلب محترمة تليق بشغلك.
            </p>
            <ul className="ed-about__points">
              {ABOUT_POINTS.map((point) => (
                <li key={point}>
                  <IconCheck className="ed-about__check" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <SignInButton signedIn={signedIn} size="lg" />
          </div>

          <figure className="ed-about__visual" data-reveal>
            <ul className="ed-gallery">
              {CATEGORIES.map((category) => (
                <li className="ed-gallery__item" key={category.slug}>
                  <img
                    src={category.src}
                    alt={`${category.ar} — قطع غيار سيارات من الدكتور`}
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="240"
                  />
                  <span>{category.ar}</span>
                </li>
              ))}
            </ul>
            <figcaption>
              <span className="ed-about__badge">
                <IconShield />
              </span>
              كل صنف على المنصة موصوف بالماركة والموديل والفئة وبلد المنشأ.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* -------------------------------------------------------------- cta band */}
      <section className="ed-band">
        <div className="ed-band__grid" aria-hidden="true" />
        <div className="ed-shell ed-band__inner" data-reveal>
          <div className="ed-band__logo">
            <img src={logo} alt={BRAND.latin} />
          </div>
          <h2 className="ed-band__title">جاهز تبدأ؟ مكالمة واحدة وتبقى عميل عندنا.</h2>
          <p className="ed-band__lead">
            اتصل بينا لفتح حسابك، وبعدها المنصة شغالة طول الوقت من أي جهاز ومن غير أي تطبيق تنزّله.
          </p>
          <div className="ed-band__actions">
            <CallButton size="lg" variant="light" />
            <SignInButton signedIn={signedIn} size="lg" variant="ghost" />
          </div>
          <span className="ed-band__hours">
            <IconClock />
            {CONTACT.hoursLong}
          </span>
        </div>
      </section>

      {/* -------------------------------------------------------------------- faq */}
      <section className="ed-section" id="faq">
        <div className="ed-shell ed-shell--narrow">
          <SectionHead eyebrow="أسئلة شائعة" title="أكتر حاجات العملاء بيسألوا عنها" />

          <div className="ed-faq" data-reveal>
            {FAQ.map((item, index) => {
              const open = openFaq === index;
              return (
                <div className={`ed-faq__item ${open ? 'is-open' : ''}`} key={item.q}>
                  <button
                    type="button"
                    className="ed-faq__q"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={open}
                  >
                    <span>{item.q}</span>
                    <IconPlus className="ed-faq__sign" />
                  </button>
                  <div className="ed-faq__a">
                    <p>{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ed-inline-cta ed-inline-cta--center" data-reveal>
            <p>لسه عندك سؤال؟</p>
            <CallButton />
            <SignInButton signedIn={signedIn} variant="outline" />
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------------- footer */}
      <footer className="ed-footer">
        <div className="ed-shell ed-footer__inner">
          <div className="ed-footer__brand">
            <div className="ed-footer__logo">
              <img src={logo} alt={`${BRAND.latin} — ${BRAND.tagline}`} />
            </div>
            <p className="ed-footer__about">
              {BRAND.legalName} <span dir="ltr">({BRAND.legalNameLatin})</span> — المعروفة باسم «
              {BRAND.knownAs}» / El Doctor. قطع غيار السيارات من سنة {BRAND.since}.
            </p>
          </div>

          <nav className="ed-footer__nav" aria-label="روابط الصفحة">
            <span className="ed-footer__heading">الصفحة</span>
            {FOOTER_LINKS.map((link) => (
              <a key={link.id} href={`#${link.id}`} onClick={(e) => scrollTo(e, link.id)}>
                {link.label}
              </a>
            ))}
          </nav>

          <div className="ed-footer__contact">
            <span className="ed-footer__heading">للتواصل</span>
            <a href={`tel:${CONTACT.tel}`} className="ed-footer__phone">
              <IconPhone />
              <span dir="ltr">{CONTACT.phone}</span>
            </a>
            <span className="ed-footer__hours">
              <IconClock />
              <span>{CONTACT.hoursLong}</span>
            </span>
          </div>

          <div className="ed-footer__cta">
            <span className="ed-footer__heading">بوابة العملاء</span>
            <p>عندك حساب؟ ادخل وشوف أسعارك وابعت طلبيتك. لسه من غير حساب؟ اتصل بينا.</p>
            <SignInButton signedIn={signedIn} variant="light" block />
          </div>
        </div>

        <div className="ed-shell ed-footer__bottom">
          <span>
            © {new Date().getFullYear()} {BRAND.legalName} — جميع الحقوق محفوظة.
          </span>
          <span className="ed-footer__latin">{BRAND.latin} · AUTO PARTS SUPPLY</span>
        </div>
      </footer>

      {/* Mobile-only: the sign-in action stays one thumb away at every scroll depth. */}
      <div className={`ed-sticky ${scrolled ? 'is-visible' : ''}`}>
        <CallButton size="lg" variant="primary" showNumber={false} />
        <SignInButton signedIn={signedIn} size="lg" variant="outline" />
      </div>
    </div>
  );
};

export default Landing;
